import { useMemo } from "react";
import { ListOrdered, Trophy } from "lucide-react";
import { nationalFlag } from "@/lib/flags";
import { conferenceEligibleTeamIds } from "@/lib/conferencePool";
import { openExternalLink } from "@/lib/externalLink";

/**
 * Per-group W-D-L-Pts standings widget with score differential.
 *
 * Renders for tournaments with group/season play:
 * - World Cup template → one table per group letter (A, B, C, …)
 * - Custom `group_knockout` → same
 * - Custom `round_robin` → single "Regular Season" table (all teams)
 *
 * Hidden for `knockout_only` and `march_madness` (no group/season concept).
 *
 * Sort order: Pts DESC → GD DESC → GF DESC → Wins DESC → Name ASC.
 * GD (goal/score differential) breaks point ties — useful for football,
 * basketball season play, etc.
 */
export default function GroupStandings({ tournament, myTeamIds = [] }) {
    const standings = useMemo(() => {
        if (!tournament || !tournament.teams) return [];
        const fmt = tournament.format;
        const template = tournament.template;
        const hasGroup =
            template === "world_cup" ||
            template === "olympics" ||
            template === "football_playoffs" ||
            fmt === "group_knockout" ||
            fmt === "round_robin";
        if (!hasGroup) return [];

        const scoring = tournament.scoring || {};
        const winPts = scoring.win_points ?? 3;
        const drawPts = scoring.draw_points ?? 1;
        const lossPts = scoring.loss_points ?? 0;
        // Score differential (GF/GA/GD) is only meaningful for pre-built templates
        // where "score" maps to a real-world stat (goals, points). Hidden for
        // custom tournaments since the score field there is opaque.
        const isCustom = template === "custom";
        // iter87.368 — Football (CFB Weekly Spread Boost) used to need
        // W-L / PF / PA here; that template has been removed entirely.
        // NFL renders via a dedicated component (see DashboardTabs), so
        // nothing maps into `isFootball` today — kept as a toggle point
        // in case a future football template needs this same treatment.
        const FOOTBALL_TEMPLATES = new Set([]);
        const isFootball = FOOTBALL_TEMPLATES.has(template);

        const groups = {};
        // iter87.115 — For conference-pool tournaments, filter the
        // standings to only the pool's teams. Null return means no
        // filtering — render everything.
        const eligibleIds = conferenceEligibleTeamIds(tournament);
        // iter87.26 — Season/round-robin's pseudo-group label was
        // "Standings" — a duplicate of the Takeover "STANDINGS" title.
        // Derive a league-specific label from the tournament template so
        // Serie A shows "Serie A Table", Premier League shows
        // "Premier League Table", NFL shows "NFL Season", etc. Group-
        // stage tournaments (World Cup / Euro) keep their per-group
        // "Group A" / "Group B" labels — those aren't duplicates.
        const LEAGUE_LABELS = {
            premier_league_api: "Premier League",
            la_liga_api: "La Liga",
            champions_league_api: "Champions League",
            "pickem:premier_league_api": "Premier League",
            "pickem:la_liga_api": "La Liga",
            "pickem:champions_league_api": "Champions League",
            euro_super_league: "Euro Super League",
            nfl_season_api: "NFL Season",
            "pickem:nfl_season_api": "NFL Season",
        };
        const seasonLabel = (() => {
            const tpl = tournament.template;
            if (tpl && LEAGUE_LABELS[tpl]) return `${LEAGUE_LABELS[tpl]} Table`;
            if ((tournament.name || "").trim()) return `${tournament.name.trim()} Table`;
            return "Table";
        })();
        // iter87.178 — GotSport tournaments mirror GotSport's numbers
        // verbatim (rank + PTS + W/D/L) via `gotsport_*` fields
        // stamped by the importer. Detect by checking whether any
        // team has a captured `gotsport_rank`.
        const isGotSport = (tournament.teams || []).some(
            (t) => t.gotsport_rank != null
        );
        for (const t of tournament.teams) {
            if (eligibleIds && !eligibleIds.has(t.id)) continue;
            // For round_robin / olympics / football_playoffs without explicit groups,
            // bucket everyone into a single pseudo-group labeled by the
            // detected league (see LEAGUE_LABELS above).
            const key = fmt === "round_robin"
                ? seasonLabel
                : (t.group || "—");
            if (!groups[key]) groups[key] = [];
            const w = isGotSport ? (t.gotsport_w ?? 0) : (t.group_wins || 0);
            const d = isGotSport ? (t.gotsport_d ?? 0) : (t.group_draws || 0);
            const l = isGotSport ? (t.gotsport_l ?? 0) : (t.group_losses || 0);
            const gf = isGotSport ? (t.gotsport_gf ?? 0) : (t.group_for || 0);
            const ga = isGotSport ? (t.gotsport_ga ?? 0) : (t.group_against || 0);
            const pts = isGotSport
                ? (t.gotsport_pts ?? 0)
                : w * winPts + d * drawPts + l * lossPts;
            groups[key].push({
                ...t,
                w, d, l, gf, ga,
                gd: gf - ga,
                played: w + d + l,
                pts,
                // iter87.178 — Trust GotSport's post-tiebreaker rank
                // when present so the Standings tile order mirrors
                // GotSport exactly, even when the tournament uses a
                // non-3/1/0 point system or custom tie-breakers.
                _gs_rank: t.gotsport_rank,
            });
        }
        for (const k of Object.keys(groups)) {
            groups[k].sort((a, b) => {
                // iter87.178 — GotSport-driven ordering short-circuits
                // every other tiebreaker path when both rows have a
                // GS rank. Falls through to legacy sort for teams
                // missing a rank (pre-iter87.178 imports, non-GS
                // rows).
                if (a._gs_rank != null && b._gs_rank != null) {
                    return a._gs_rank - b._gs_rank;
                }
                if (isFootball) {
                    // W-L (win pct) → wins → PF-PA differential → PF → name
                    const bpct = b.played ? b.w / b.played : 0;
                    const apct = a.played ? a.w / a.played : 0;
                    if (bpct !== apct) return bpct - apct;
                    if (b.w !== a.w) return b.w - a.w;
                    const bdiff = b.gf - b.ga;
                    const adiff = a.gf - a.ga;
                    if (bdiff !== adiff) return bdiff - adiff;
                    if (b.gf !== a.gf) return b.gf - a.gf;
                    return (a.name || "").localeCompare(b.name || "");
                }
                if (b.pts !== a.pts) return b.pts - a.pts;
                if (!isCustom) {
                    if (b.gd !== a.gd) return b.gd - a.gd;       // score differential tiebreaker
                    if (b.gf !== a.gf) return b.gf - a.gf;       // then goals scored
                }
                if (b.w !== a.w) return b.w - a.w;
                return (a.name || "").localeCompare(b.name || "");
            });
        }
        return { groups: Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)), isCustom, isFootball, isGotSport };
    }, [tournament]);

    const groupEntries = standings.groups || [];
    const isCustom = standings.isCustom;
    const isGotSport = standings.isGotSport;
    const isFootball = standings.isFootball;

    if (groupEntries.length === 0) {
        return (
            <div className="brutal-card p-6 text-center text-zinc-500" data-testid="group-standings-empty">
                <ListOrdered className="w-10 h-10 mx-auto text-zinc-700 mb-2" />
                <p>Standings aren't tracked for this tournament format.</p>
                <p className="text-xs mt-1">Round-robin and group-stage tournaments show W-D-L-Pts tables here.</p>
            </div>
        );
    }

    const myIdSet = new Set(myTeamIds);
    const fmt = tournament?.format;

    const fmtGd = (n) => (n > 0 ? `+${n}` : `${n}`);

    const sortDesc = isGotSport
        ? "Order + GS PTS pulled directly from GotSport (post-tiebreakers)."
        : isFootball
            ? "Sorted by win pct → wins → point differential → points for."
            : isCustom
                ? "Sorted by points → wins → name."
                : "Sorted by points → score differential → goals for → wins.";

    return (
        <div className="space-y-4" data-testid="group-standings-view">
            <p className="text-xs text-zinc-500 px-1">
                {fmt === "round_robin"
                    ? `${sortDesc} Top of the table wins the tournament.`
                    : `${sortDesc} Top finishers in each group advance to the knockout bracket.`}
            </p>
            <div className={`grid gap-4 ${groupEntries.length > 2 ? "md:grid-cols-2 lg:grid-cols-3" : ""}`}>
                {groupEntries.map(([groupKey, rows]) => (
                    <div key={groupKey} className="brutal-card p-4" data-testid={`standings-group-${groupKey}`}>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2">
                                <Trophy className="w-4 h-4 text-cyber" />
                                {fmt === "round_robin" ? groupKey : `Group ${groupKey}`}
                            </h4>
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500">{rows.length} teams</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs min-w-[280px]">
                                <thead>
                                    <tr className="text-[9px] uppercase tracking-widest text-zinc-500 border-b-2 border-[#222]">
                                        <th className="text-left py-1.5 font-bold">#</th>
                                        <th className="text-left py-1.5 font-bold">Team</th>
                                        {isFootball ? (
                                            <>
                                                <th className="text-center py-1.5 font-bold w-12">W-L</th>
                                                <th className="text-center py-1.5 font-bold w-10">PF</th>
                                                <th className="text-center py-1.5 font-bold w-10">PA</th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="text-center py-1.5 font-bold w-7">P</th>
                                                <th className="text-center py-1.5 font-bold w-7">W</th>
                                                <th className="text-center py-1.5 font-bold w-7">D</th>
                                                <th className="text-center py-1.5 font-bold w-7">L</th>
                                                {!isCustom && (
                                                    <>
                                                        <th className="text-center py-1.5 font-bold w-8">GF</th>
                                                        <th className="text-center py-1.5 font-bold w-8">GA</th>
                                                        <th className="text-center py-1.5 font-bold w-9">GD</th>
                                                    </>
                                                )}
                                                <th className="text-right py-1.5 font-bold w-8" title={isGotSport ? "Points as reported by GotSport — accounts for whatever scoring system this tournament uses" : "Points"}>{isGotSport ? "GS PTS" : "Pts"}</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((t, idx) => {
                                        const isMine = myIdSet.has(t.id);
                                        const gdColor = t.gd > 0 ? "text-neon" : t.gd < 0 ? "text-blaze" : "text-zinc-400";
                                        return (
                                            <tr
                                                key={t.id}
                                                data-testid={`standings-row-${t.id}`}
                                                className={`border-b border-[#1a1a1a] hover:bg-[#0e0e0e] ${isMine ? "bg-cyber/5" : ""}`}
                                            >
                                                <td className="py-1.5 text-zinc-500 font-mono">{idx + 1}</td>
                                                <td className="py-1.5 truncate max-w-[140px]">
                                                    {(() => {
                                                        // iter87.177 — Wrap the team name in a
                                                        // link to its GotSport team page when
                                                        // available (imported via `gotsport-import`).
                                                        const gsUrl = (t.gotsport_team_id && tournament?.gotsport_event_id)
                                                            ? `https://system.gotsport.com/org_event/events/${tournament.gotsport_event_id}/schedules?team=${t.gotsport_team_id}`
                                                            : null;
                                                        const inner = (
                                                            <span className={isMine ? "text-cyber font-bold" : ""}>
                                                                {nationalFlag(t.flag)} {t.name}
                                                            </span>
                                                        );
                                                        return gsUrl ? (
                                                            <a
                                                                href={gsUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => { e.preventDefault(); openExternalLink(gsUrl); }}
                                                                data-testid={`standings-gotsport-link-${t.id}`}
                                                                className="hover:text-cyber transition-colors"
                                                                title="Open on GotSport"
                                                            >
                                                                {inner}
                                                            </a>
                                                        ) : inner;
                                                    })()}
                                                    {isMine && <span className="ml-1.5 pill bg-cyber text-black text-[8px] py-0 px-1.5">you</span>}
                                                </td>
                                                {isFootball ? (
                                                    <>
                                                        <td
                                                            className="text-center py-1.5 font-mono font-bold text-cyber"
                                                            data-testid={`standings-wl-${t.id}`}
                                                        >
                                                            {t.w}-{t.l}
                                                        </td>
                                                        <td className="text-center py-1.5 font-mono text-zinc-300" data-testid={`standings-pf-${t.id}`}>{t.gf}</td>
                                                        <td className="text-center py-1.5 font-mono text-zinc-500" data-testid={`standings-pa-${t.id}`}>{t.ga}</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="text-center py-1.5 font-mono text-zinc-400">{t.played}</td>
                                                        <td className="text-center py-1.5 font-mono text-neon">{t.w}</td>
                                                        <td className="text-center py-1.5 font-mono text-zinc-300">{t.d}</td>
                                                        <td className="text-center py-1.5 font-mono text-blaze">{t.l}</td>
                                                        {!isCustom && (
                                                            <>
                                                                <td className="text-center py-1.5 font-mono text-zinc-300">{t.gf}</td>
                                                                <td className="text-center py-1.5 font-mono text-zinc-500">{t.ga}</td>
                                                                <td className={`text-center py-1.5 font-mono font-bold ${gdColor}`} data-testid={`standings-gd-${t.id}`}>{fmtGd(t.gd)}</td>
                                                            </>
                                                        )}
                                                        <td className="text-right py-1.5 font-mono font-bold text-cyber">{t.pts}</td>
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
